/* One motion state for autoplay, pointer dragging, keyboard navigation and the pause button.
   Copies fill at least one viewport beyond a full cycle. Fixed item geometry makes resize
   independent of image loading; wrapping changes only the offset, never the visible order. */
(() => {
  if (customElements.get('si-loop')) return;
  customElements.define('si-loop', class extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.si-loop__viewport');
      this.track = this.querySelector('.si-loop__track');
      this.toggle = this.querySelector('[data-loop-toggle]');
      this.controls = this.querySelector('.si-loop__controls');
      if (!this.viewport || !this.track ) return;
      this.originals = [...this.track.children].filter(el => !el.hasAttribute('data-loop-copy'));
      if (!this.originals.length) return;
      this.abort = new AbortController();
      const on = (el, event, fn, options = {}) => el.addEventListener(event, fn, { ...options, signal: this.abort.signal });
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = !this.motion.matches;
      this.visible = false;
      this.offset = 0;
      this.speed = Math.max(10, Math.min(60, Number(this.dataset.speed) || 24));
      this.buttonOnly = this.dataset.kind === 'brands';
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.viewport);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.update();
      });
      this.observer.observe(this);
      on(this.motion, 'change', () => { if (this.buttonOnly) { this.playing = !this.motion.matches; this.measure(); } else if (this.motion.matches) this.pause(); });
      on(document, 'visibilitychange', () => this.update());
      if (this.toggle) on(this.toggle, 'click', () => { this.playing = !this.playing; this.update(); });
      on(this.querySelector('[data-loop-prev]') || this, 'click', e => {
        if (e.target.closest('[data-loop-prev]')) this.move(-1);
      });
      on(this.querySelector('[data-loop-next]') || this, 'click', e => {
        if (e.target.closest('[data-loop-next]')) this.move(1);
      });
      on(this.viewport, 'dragstart', e => e.preventDefault());
      on(this.viewport, 'pointerdown', e => {
        if (this.buttonOnly && this.motion.matches) return;
        if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
        if (!this.buttonOnly) this.pause();
        this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, start: this.offset, moved: false };
        this.update();
      });
      on(this.viewport, 'pointermove', e => {
        if (!this.drag || e.pointerId !== this.drag.id) return;
        const dx = e.clientX - this.drag.x, dy = e.clientY - this.drag.y;
        if (!this.drag.moved && (Math.abs(dx) < 6 || Math.abs(dy) > Math.abs(dx))) return;
        this.drag.moved = true;
        if (!this.viewport.hasPointerCapture(e.pointerId)) this.viewport.setPointerCapture(e.pointerId);
        this.classList.add('is-dragging');
        this.offset = this.drag.start - dx;
        this.paint();
      });
      const end = e => {
        if (!this.drag || e.pointerId !== this.drag.id) return;
        this.blockClick = this.drag.moved;
        if (this.viewport.hasPointerCapture(e.pointerId)) this.viewport.releasePointerCapture(e.pointerId);
        this.drag = null;
        this.classList.remove('is-dragging');
        this.update();
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.blockClick = false; }, 0);
      };
      on(window, 'pointerup', end);
      on(this.viewport, 'pointercancel', end);
      on(this.viewport, 'click', e => {
        if (this.blockClick) { e.preventDefault(); e.stopPropagation(); this.blockClick = false; }
      }, { capture: true });
      on(this.viewport, 'wheel', e => {
        if (this.buttonOnly && this.motion.matches) return;
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          if (!this.buttonOnly) this.pause();
          this.offset += e.deltaX; this.paint(); e.preventDefault();
        }
      }, { passive: false });
      on(this.viewport, 'focusin', e => {
        if (this.buttonOnly && this.motion.matches) return;
        if (!this.buttonOnly) this.pause();
        const item = this.originals.find(el => el.contains(e.target));
        if (item) {
          const left = item.offsetLeft - this.track.offsetLeft;
          if (left < this.offset || left + item.offsetWidth > this.offset + this.viewport.clientWidth) this.offset = left;
          this.viewport.scrollLeft = 0;
          this.paint();
        }
      });
      on(this.viewport, 'keydown', e => {
        if (this.buttonOnly && this.motion.matches) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.move(e.key === 'ArrowRight' ? 1 : -1); }
      });
      // Hover and focus do not stop brand autoplay; selection in the theme editor is inspectable.
      on(document, 'shopify:block:select', e => {
        const item = this.originals.find(el => el === e.target || el.contains(e.target));
        if (item) { this.pause(); this.offset = item.offsetLeft - this.track.offsetLeft; this.paint(); }
      });
      this.measure();
    }
    measure() {
      const width = this.viewport.clientWidth;
      if (!width) return;
      const oldCycle = this.cycle || 1;
      const fraction = (this.offset % oldCycle) / oldCycle;
      this.track.querySelectorAll('[data-loop-copy]').forEach(el => el.remove());
      this.gap = this.dataset.kind === 'brands' ? 24 : (width < 650 ? 14 : 20);
      this.itemWidth = this.dataset.kind === 'brands' ? (width < 650 ? 148 : 168) : (width < 650 ? width * .8 : (width < 1000 ? (width - 40) / 3 : (width - 60) / 4));
      this.style.setProperty('--loop-item', this.itemWidth + 'px');
      this.style.setProperty('--loop-gap', this.gap + 'px');
      this.cycle = this.originals.length * (this.itemWidth + this.gap);
      this.offset = fraction * this.cycle;
      const staticBrands = this.buttonOnly && this.motion.matches;
      this.toggleAttribute('data-static', staticBrands);
      if (staticBrands) this.offset = 0;
      const copies = staticBrands ? 0 : Math.ceil(width / this.cycle) + 1;
      for (let group = 1; group <= copies; group++) {
        this.originals.forEach(original => {
          const copy = original.cloneNode(true);
          copy.setAttribute('data-loop-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          const nodes = [copy, ...copy.querySelectorAll('*')];
          const ids = new Map(nodes.filter(el => el.id).map(el => [el.id, el.id + '-loop-' + group]));
          for (const el of nodes) {
            if (el.id) el.id = ids.get(el.id);
            el.removeAttribute('data-shopify-editor-block');
            for (const key of ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for']) {
              if (el.hasAttribute(key)) el.setAttribute(key, el.getAttribute(key).split(' ').map(id => ids.get(id) || id).join(' '));
            }
            if (el.matches('a,button,input,select,textarea,[tabindex]')) el.setAttribute('tabindex', '-1');
          }
          this.track.append(copy);
        });
      }
      this.setAttribute('data-ready', '');
      if (this.controls) this.controls.hidden = false;
      this.paint();
      this.update();
    }
    pause() { this.playing = false; this.update(); }
    move(direction) { if (!this.buttonOnly) this.pause(); this.offset += direction * (this.itemWidth + this.gap); this.paint(); }
    paint() {
      if (!this.cycle) return;
      this.offset = ((this.offset % this.cycle) + this.cycle) % this.cycle;
      this.track.style.transform = `translate3d(${-this.offset}px,0,0)`;
    }
    update() {
      const running = this.playing && this.visible && !document.hidden && !this.drag;
      if (this.toggle) this.toggle.textContent = this.playing ? 'Zatrzymaj' : 'Wznów';
      if (this.toggle) this.toggle.setAttribute('aria-label', (this.playing ? 'Zatrzymaj' : 'Wznów') + (this.dataset.kind === 'brands' ? ' przewijanie marek' : ' przewijanie produktów'));
      this.dataset.playing = String(running);
      cancelAnimationFrame(this.frame);
      this.last = null;
      if (!running) return;
      const tick = now => {
        if (this.last !== null) { this.offset += Math.min(now - this.last, 64) * this.speed / 1000; this.paint(); }
        this.last = now;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    disconnectedCallback() {
      this.abort?.abort(); this.resize?.disconnect(); this.observer?.disconnect();
      cancelAnimationFrame(this.frame); clearTimeout(this.clickTimer);
      this.track?.querySelectorAll('[data-loop-copy]').forEach(el => el.remove());
    }
  });
})();
